// The part of the screen the keyboard leaves free. iOS Safari never shrinks
// the layout viewport (100dvh) when the keyboard opens — only the VISUAL
// viewport shrinks — so a bar docked at the bottom ends up under the
// keyboard. The new phone editor sizes its whole frame to this instead.
import { useEffect, useState } from 'react';

export type VisualViewportBox = { height: number; offsetTop: number; keyboardOpen: boolean };

const KEYBOARD_MIN_PX = 120;

function read(): VisualViewportBox {
  if (typeof window === 'undefined') return { height: 0, offsetTop: 0, keyboardOpen: false };
  const vv = window.visualViewport;
  const h = vv?.height ?? window.innerHeight;
  return {
    height: Math.round(h),
    offsetTop: Math.round(vv?.offsetTop ?? 0),
    keyboardOpen: window.innerHeight - h > KEYBOARD_MIN_PX,
  };
}

export function useVisualViewport(enabled: boolean): VisualViewportBox {
  const [box, setBox] = useState<VisualViewportBox>(read);
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const vv = window.visualViewport;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = read();
        setBox((prev) => (prev.height === next.height && prev.offsetTop === next.offsetTop && prev.keyboardOpen === next.keyboardOpen ? prev : next));
      });
    };
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled]);
  return box;
}
